import React, { useMemo } from 'react';
import { LuActivity, LuCheck } from 'react-icons/lu';
import { FiAlertTriangle } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const PipelineHealthCard = ({ opportunities }) => {
  const healthMetrics = useMemo(() => {
    const activeOpps = opportunities.filter(
      opp => !['Closed Won', 'Closed Lost'].includes(opp.stage)
    );

    const staleOpps = activeOpps.filter(opp => {
      const daysSinceUpdate = Math.floor(
        (new Date() - new Date(opp.updated_at)) / (1000 * 60 * 60 * 24)
      );
      return daysSinceUpdate > 30;
    });

    const hotOpps = activeOpps.filter(
      opp => opp.stage === 'Negotiation' || opp.stage === 'Proposal'
    );

    const pipelineCoverage = activeOpps.reduce(
      (sum, opp) => sum + (parseFloat(opp.amount) || 0), 
      0
    );

    const wonOpps = opportunities.filter(opp => opp.stage === 'Closed Won');
    const targetRevenue = wonOpps.reduce(
      (sum, opp) => sum + (parseFloat(opp.amount) || 0), 
      0
    ) * 1.5; // Assume 1.5x target

    const coverageRatio = targetRevenue > 0 
      ? (pipelineCoverage / targetRevenue) * 100 
      : 0;

    return {
      totalActive: activeOpps.length,
      staleDeals: staleOpps.length,
      hotDeals: hotOpps.length,
      coverageRatio: coverageRatio.toFixed(0),
      healthScore: Math.min(100, Math.round(
        (hotOpps.length * 30) + 
        ((activeOpps.length - staleOpps.length) / Math.max(activeOpps.length, 1) * 40) +
        (Math.min(coverageRatio, 100) * 0.3)
      ))
    };
  }, [opportunities]);

  const getHealthColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <SafeIcon icon={LuActivity} className="h-5 w-5 text-blue-500 mr-2" />
          Pipeline Health
        </h3>
        <div className={`px-3 py-1 rounded-full ${getHealthBg(healthMetrics.healthScore)}`}>
          <span className={`text-sm font-bold ${getHealthColor(healthMetrics.healthScore)}`}>
            {healthMetrics.healthScore}/100
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Active Deals */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={LuCheck} className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-gray-700">Active Deals</span>
          </div>
          <span className="text-lg font-bold text-gray-900">
            {healthMetrics.totalActive}
          </span>
        </div>

        {/* Hot Deals */}
        <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="h-5 w-5 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-700">Hot Deals</span>
          </div>
          <span className="text-lg font-bold text-gray-900">
            {healthMetrics.hotDeals}
          </span>
        </div>

        {/* Stale Deals */}
        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiAlertTriangle} className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-gray-700">Stale Deals (30+ days)</span>
          </div>
          <span className="text-lg font-bold text-gray-900">
            {healthMetrics.staleDeals}
          </span>
        </div>

        {/* Pipeline Coverage */}
        <div className="pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Pipeline Coverage</span>
            <span className="text-sm font-bold text-gray-900">
              {healthMetrics.coverageRatio}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                healthMetrics.coverageRatio >= 100 
                  ? 'bg-green-500' 
                  : healthMetrics.coverageRatio >= 70 
                  ? 'bg-yellow-500' 
                  : 'bg-red-500'
              }`}
              style={{ width: `${Math.min(healthMetrics.coverageRatio, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Target: 100% coverage ratio
          </p>
        </div>
      </div>
    </div>
  );
};

export default PipelineHealthCard;